import React, { useEffect, useRef, useState } from "react";
import { Building2, Upload, Save, Image as ImageIcon, MessageCircle } from "lucide-react";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import ContratoEditor from "../components/contrato/ContratoEditor";
import DocumentoEditor from "../components/documento/DocumentoEditor";
import HeaderFooterEditor from "../components/documento/HeaderFooterEditor";
import { useToast } from "../hooks/use-toast";

const inputCls = "w-full px-3.5 py-2.5 rounded-lg border border-slate-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm";

const DOCS_TIPOS = [
  { key: "declaracao_autorizadora", label: "Declaração Autorizadora" },
  { key: "ficha_cadastral", label: "Ficha Cadastral" },
  { key: "ficha_internacao", label: "Ficha de Internação" },
  { key: "prescricao_contencao", label: "Autorização de Contenção" },
  { key: "uso_imagem", label: "Uso de Imagem" },
];

const campos = [
  ["nome", "Nome da empresa", "sm:col-span-2"],
  ["cnpj", "CNPJ", ""],
  ["email", "E-mail", ""],
  ["telefone", "Telefone", ""],
  ["telefone2", "Telefone 2 / Celular", ""],
  ["endereco", "Endereço", "sm:col-span-2"],
  ["cep", "CEP / Cidade-UF", ""],
  ["site", "Site", ""],
];

const Configuracoes = () => {
  const { empresa, salvarEmpresa, testarAvisoContas } = useApp();
  const { toast } = useToast();
  const [form, setForm] = useState(empresa || {});
  const [salvando, setSalvando] = useState(false);
  const [testando, setTestando] = useState(false);
  const [docKey, setDocKey] = useState(DOCS_TIPOS[0].key);
  const logoRef = useRef(null);

  useEffect(() => { if (empresa) setForm(empresa); }, [empresa]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const testarAviso = async () => {
    setTestando(true);
    try {
      const r = await testarAvisoContas();
      const msgs = { enviado: "Aviso enviado com sucesso!", sem_contas: "Nenhuma conta a vencer nos próximos dias.", pausado: "Aviso está desativado — ative e salve primeiro.", erro: r.erro || "Falha no envio." };
      toast({ title: msgs[r.status] || "Resultado do teste", description: r.status === "erro" ? r.erro : "" });
    } catch (e) {
      toast({ title: "Erro ao testar", description: e?.response?.data?.detail || "" });
    }
    setTestando(false);
  };

  const comprimirImagem = (file, maxDim = 320) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const escala = maxDim / Math.max(width, height);
          width = Math.round(width * escala);
          height = Math.round(height * escala);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        // PNG preserva transparência; se ficar grande, cai para JPEG
        let out = canvas.toDataURL("image/png");
        if (out.length > 120000) out = canvas.toDataURL("image/jpeg", 0.85);
        resolve(out);
      };
      img.onerror = () => resolve(reader.result);
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const comprimida = await comprimirImagem(file);
    set("logo", comprimida);
    if (logoRef.current) logoRef.current.value = "";
  };

  const salvar = async () => {
    setSalvando(true);
    try {
      await salvarEmpresa(form);
      toast({ title: "Configurações salvas", description: "Os dados aparecerão nos timbrados, faturas e relatórios." });
    } catch (e) {
      toast({ title: "Erro ao salvar", description: e?.response?.data?.detail || "Tente novamente." });
    } finally { setSalvando(false); }
  };

  return (
    <div className="space-y-6" data-testid="configuracoes-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><Building2 className="w-6 h-6 text-blue-600" /> Configurações</h1>
        <p className="text-slate-400 text-sm mt-0.5">Dados da empresa e modelo do contrato usados nos timbrados, PDFs, faturas e relatórios.</p>
      </div>

      <Tabs defaultValue="empresa" className="w-full">
        <TabsList data-testid="config-tabs">
          <TabsTrigger value="empresa" data-testid="tab-empresa">Empresa</TabsTrigger>
          <TabsTrigger value="contrato" data-testid="tab-contrato">Modelo de Contrato</TabsTrigger>
          <TabsTrigger value="documentos" data-testid="tab-documentos">Documentos</TabsTrigger>
          <TabsTrigger value="cabecalho" data-testid="tab-cabecalho">Cabeçalho e Rodapé</TabsTrigger>
        </TabsList>

        <TabsContent value="empresa" className="mt-4 max-w-3xl">
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6 space-y-5">
        {/* Logo */}
        <div className="flex items-center gap-5">
          <div className="w-24 h-24 rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
            {form.logo ? (
              <img src={form.logo} alt="logo" className="w-full h-full object-contain" data-testid="logo-preview" />
            ) : (
              <ImageIcon className="w-8 h-8 text-slate-300" />
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-slate-700 mb-1">Logotipo</p>
            <p className="text-xs text-slate-400 mb-2">PNG ou JPG. Aparece no topo dos documentos.</p>
            <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="logo-input" />
            <div className="flex gap-2">
              <Button variant="outline" className="gap-2" onClick={() => logoRef.current?.click()} data-testid="logo-upload-btn"><Upload className="w-4 h-4" /> Enviar logo</Button>
              {form.logo && <Button variant="ghost" className="text-red-500" onClick={() => set("logo", "")}>Remover</Button>}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {campos.map(([k, label, span]) => (
            <div key={k} className={span}>
              <label className="text-sm text-slate-600 mb-1 block">{label}</label>
              <input className={inputCls} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`empresa-${k}`} />
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4 space-y-3" data-testid="aviso-contas-config">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-emerald-600" />
            <p className="text-sm font-semibold text-slate-800">Aviso de contas a pagar (WhatsApp)</p>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={!!form.avisoContasAtivo} onChange={(e) => set("avisoContasAtivo", e.target.checked)} data-testid="toggle-aviso-contas" />
            Enviar lembrete automático das contas que estão vencendo
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-slate-600 mb-1 block">Número que recebe o aviso</label>
              <input className={inputCls} value={form.avisoContasTelefone || ""} onChange={(e) => set("avisoContasTelefone", e.target.value)} placeholder="ex.: 11948882374" data-testid="aviso-contas-telefone" />
            </div>
            <div className="flex items-end">
              <Button type="button" variant="outline" className="gap-2" disabled={testando} onClick={testarAviso} data-testid="testar-aviso-btn">
                <MessageCircle className="w-4 h-4" /> {testando ? "Enviando..." : "Testar envio agora"}
              </Button>
            </div>
          </div>
          <p className="text-[11px] text-slate-400">Salve as configurações antes de testar. O envio depende de um template aprovado na Meta (WhatsApp).</p>
        </div>

        <div className="flex justify-end pt-2">
          <Button className="bg-blue-600 hover:bg-blue-700 gap-2" disabled={salvando} onClick={salvar} data-testid="salvar-empresa-btn">
            <Save className="w-4 h-4" /> {salvando ? "Salvando..." : "Salvar configurações"}
          </Button>
        </div>
          </div>
        </TabsContent>

        <TabsContent value="contrato" className="mt-4 max-w-5xl">
          <ContratoEditor />
        </TabsContent>

        <TabsContent value="documentos" className="mt-4 max-w-5xl">
          <div className="flex flex-wrap gap-2 mb-4" data-testid="documentos-seletor">
            {DOCS_TIPOS.map((d) => (
              <button
                key={d.key}
                type="button"
                onClick={() => setDocKey(d.key)}
                data-testid={`doc-tipo-${d.key}`}
                className={`px-3.5 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  docKey === d.key
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
          <DocumentoEditor key={docKey} docKey={docKey} />
        </TabsContent>

        <TabsContent value="cabecalho" className="mt-4 max-w-5xl">
          <HeaderFooterEditor />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Configuracoes;
